/* The lighting team's fixture workbook (Duncan 2026-10-22: "an Excel template of fixtures to replace", the same for
   every lighting job): the sheet whose header row has Line # · Campus · Building · Map ID · Description · EXT Qty ·
   EXT Fixture Code · Existing Fixture Type · Comments · PRO Qty · PRO Fixt Tag · Proposed Fixture Type · Comments ·
   Install Occ Sensor? -- read by those headings, so a moved column doesn't matter. The Comments after the existing
   fixture are its comments; the next ones, the proposed fixture's. Only these columns ever leave the browser: never a
   price, cost or hours, whatever else the workbook holds. Read here, in the browser (the server only checks and keeps
   what it's sent: routes/taskWork.js). */
import * as XLSX from 'xlsx';

const key = (v) => String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9#?]+/g, ' ').trim();
// Each field's headings, as they're written (and close kin).
const HEADS = {
  line_no: ['line #', 'line', 'line no', 'line number'],
  site: ['campus', 'site'],
  building: ['building'],
  map_id: ['map id', 'map'],
  area: ['description', 'room', 'area', 'room area', 'room office area'],
  ext_qty: ['ext qty', 'existing qty', 'ext quantity'],
  ext_code: ['ext fixture code', 'existing fixture code', 'ext code'],
  ext_type: ['existing fixture type', 'ext fixture type', 'existing type'],
  pro_qty: ['pro qty', 'proposed qty', 'pro quantity'],
  pro_tag: ['pro fixt tag', 'pro fixture tag', 'proposed tag', 'pro tag'],
  pro_type: ['proposed fixture type', 'pro fixture type', 'proposed type'],
  occ_sensor: ['install occ sensor?', 'install occ sensor', 'occ sensor', 'occupancy sensor'],
};
const MUST = ['site', 'area'];
const text = (v) => {
  if (v == null) return null;
  const s = String(v).replace(/\s+/g, ' ').trim();
  return s || null;
};
const qty = (v) => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
};

/** Where each field sits in a header row (column indexes), or null when it isn't a header row. */
function columnsOf(row) {
  const heads = row.map(key);
  const at = {};
  Object.entries(HEADS).forEach(([field, names]) => {
    const i = heads.findIndex((h) => names.includes(h));
    if (i >= 0) at[field] = i;
  });
  if (!MUST.every((f) => at[f] != null) || (at.ext_qty == null && at.pro_qty == null)) return null;
  // The two Comments: the first after the existing fixture's columns, then the first after the proposed one's.
  const comments = heads.map((h, i) => (h === 'comments' || h === 'comment' || h === 'notes' ? i : -1)).filter((i) => i >= 0);
  const extEnd = Math.max(at.ext_type ?? -1, at.ext_code ?? -1, at.ext_qty ?? -1);
  const proEnd = Math.max(at.pro_type ?? -1, at.pro_tag ?? -1, at.pro_qty ?? -1);
  const ext = comments.find((i) => i > extEnd && (at.pro_qty == null || i < at.pro_qty));
  const pro = comments.find((i) => i > proEnd);
  if (ext != null) at.ext_comments = ext;
  if (pro != null) at.pro_comments = pro;
  return at;
}

/**
 * The workbook's lines: { sheet, lines: [line], sites: [{ name, lines, ext, pro, buildings }] }, or { error }.
 * A line is a row with its place (site, building or room) under the header row; a Map ID of 0 is "none".
 */
export function readFixtureWorkbook(arrayBuffer) {
  let wb;
  try {
    wb = XLSX.read(arrayBuffer, { type: 'array' });
  } catch (e) {
    return { error: 'This file isn’t a workbook Summit can read. Use the lighting team’s Excel file (.xlsx).' };
  }
  for (const name of wb.SheetNames) {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: null, raw: true, blankrows: false });
    for (let h = 0; h < Math.min(rows.length, 15); h += 1) {
      const at = columnsOf(rows[h] || []);
      if (!at) continue;
      const cell = (r, f) => (at[f] == null ? null : r[at[f]]);
      const lines = [];
      for (const r of rows.slice(h + 1)) {
        if (!r) continue;
        const line = {
          line_no: qty(cell(r, 'line_no')),
          site: text(cell(r, 'site')), building: text(cell(r, 'building')),
          map_id: (() => { const m = text(cell(r, 'map_id')); return m && !/^0+$/.test(m) ? m : null; })(),
          area: text(cell(r, 'area')),
          ext_qty: qty(cell(r, 'ext_qty')), ext_code: text(cell(r, 'ext_code')), ext_type: text(cell(r, 'ext_type')), ext_comments: text(cell(r, 'ext_comments')),
          pro_qty: qty(cell(r, 'pro_qty')), pro_tag: text(cell(r, 'pro_tag')), pro_type: text(cell(r, 'pro_type')), pro_comments: text(cell(r, 'pro_comments')),
          occ_sensor: text(cell(r, 'occ_sensor')),
        };
        if (!line.site && !line.building && !line.area) continue; // a blank or totals row
        lines.push(line);
      }
      if (!lines.length) return { error: `The “${name}” sheet has its headings but no lines under them.` };
      const bySite = new Map();
      lines.forEach((l) => {
        const k = l.site || '(no site)';
        const s = bySite.get(k) || { name: k, lines: 0, ext: 0, pro: 0, buildings: new Set() };
        s.lines += 1;
        s.ext += l.ext_qty || 0;
        s.pro += l.pro_qty || 0;
        if (l.building) s.buildings.add(l.building);
        bySite.set(k, s);
      });
      return { sheet: name, lines, sites: [...bySite.values()].map((s) => ({ ...s, buildings: s.buildings.size })) };
    }
  }
  return { error: 'No sheet has the fixture headings (Campus, Description, EXT Qty / PRO Qty). Use the lighting team’s workbook.' };
}

// Words that don't tell one place from another.
const COMMON = new Set('the of and at a an in on school elementary middle high junior es ms hs isd cisd library office building campus center centre hq main admin administration park city'.split(' '));
const words = (s) => key(s).split(' ').filter((w) => w.length > 1 && !COMMON.has(w));

/** The site that's this task's, by its location's (or name's) own words: "Seemes Library" for a task at the
    Seemes Library. Null when none says so -- then it's picked by hand. */
export function suggestSite(sites, task) {
  const want = new Set([...words(task && task.location), ...words(task && task.name)]);
  let best = null;
  sites.forEach((s) => {
    const hits = words(s.name).filter((w) => want.has(w)).length;
    if (hits && (!best || hits > best.hits)) best = { name: s.name, hits };
  });
  return best ? best.name : null;
}
